import React from 'react';
import { t } from '@lingui/macro';

import OverflowTextModal from '@rover/components/src/OverflowTextModal';
import { Box, Flex, Heading } from '@rover/kibble/core';

import { SitterProfileVersionOptions } from '../../constants';
import { useRevampProfileContext } from '../../hooks/useRevampProfileContext';
import { sendSectionInteractionEvent } from '../../utils/sendSectionInteractionEvent';
import AvailabilityCalendarSection from '../AvailabilityCalendarSection/AvailabilityCalendarSection';

const MOBILE_DISPLAY = ['block', 'none'];
const DESKTOP_TABLET_DISPLAY = ['none', 'block'];

interface RevampAvailabilityCalendarProps {
  language: string;
  selectedDate: Date;
  isEU?: boolean;
}

const RevampAvailabilityCalendar = ({
  language,
  selectedDate,
  isEU,
}: RevampAvailabilityCalendarProps): JSX.Element | null => {
  const { sitterData, serviceType } = useRevampProfileContext();

  const service = sitterData.services.find((s) => s.slug === serviceType);

  const calendarProps = {
    language,
    sitterProfile: sitterData,
    selectedDate,
    initialService: service,
    isEU,
    pageVariant: SitterProfileVersionOptions.REVAMP,
  };

  return (
    <Flex
      flexDirection="column"
      pt="6x"
      pb="8x"
      alignItems="flex-start"
      alignSelf="stretch"
      borderBottom="1px solid"
      borderBottomColor="separator.primary"
      data-testid="sitter-availability"
    >
      <Heading size="400" webProps={{ responsive: false }}>{t`Calendar and availability`}</Heading>
      <Box display={MOBILE_DISPLAY} width="100%">
        <AvailabilityCalendarSection {...calendarProps} numberOfMonths={1} />
      </Box>
      <Box display={DESKTOP_TABLET_DISPLAY} width="100%">
        <AvailabilityCalendarSection {...calendarProps} numberOfMonths={2} />
      </Box>
      {sitterData.availability && (
        <Box width="100%" pt="10x">
          <OverflowTextModal
            title={t`Schedule`}
            text={sitterData.availability}
            onReadMore={() => {
              sendSectionInteractionEvent('availability', {
                interactionType: 'expand',
                pageVariant: SitterProfileVersionOptions.REVAMP,
              });
            }}
          />
        </Box>
      )}
    </Flex>
  );
};

export default RevampAvailabilityCalendar;
